/**
 * Application-wide constants.
 *
 * Centralising these means a role name, a colour, or a URL path is defined
 * in exactly one place — change it here and it changes everywhere.
 */

// ── Staff Roles ────────────────────────────────────────────────────────────────
export const ROLES = {
  ADMIN:               'admin',
  WAITER:              'waiter',
  CASHIER:             'cashier',
  KITCHEN:             'kitchen',
  CHEF:                'chef',
  RECEPTION:           'reception',
  GATE:                'gate',
  HOUSEKEEPING:        'housekeeping',
  ACCOUNTANT:          'accountant',
  GIFT_SHOP:           'gift_shop',
  RESTAURANT_MANAGER:  'restaurant_manager',
  STOREKEEPER:         'storekeeper',
  MARKETING:           'marketing',
  HR:                  'hr',
  SWIMMING_ATTENDANT:  'swimming_attendant',
  KIDS_AMENITIES:      'kids_amenities',
};

export const ROLE_LABELS = {
  [ROLES.ADMIN]:               'Administrator',
  [ROLES.WAITER]:              'Waiter',
  [ROLES.CASHIER]:             'Cashier',
  [ROLES.KITCHEN]:             'Kitchen Staff',
  [ROLES.CHEF]:                'Chef',
  [ROLES.RECEPTION]:           'Receptionist',
  [ROLES.GATE]:                'Gate / Security',
  [ROLES.HOUSEKEEPING]:        'Housekeeping',
  [ROLES.ACCOUNTANT]:          'Accountant',
  [ROLES.GIFT_SHOP]:           'Gift Shop',
  [ROLES.RESTAURANT_MANAGER]:  'Restaurant Manager',
  [ROLES.STOREKEEPER]:         'Storekeeper',
  [ROLES.MARKETING]:           'Marketing',
  [ROLES.HR]:                  'HR',
  [ROLES.SWIMMING_ATTENDANT]:  'Swimming Attendant',
  [ROLES.KIDS_AMENITIES]:      'Kids Amenities',
};

export const ROLE_COLORS = {
  [ROLES.ADMIN]:               '#c8a96e',
  [ROLES.WAITER]:              '#3498db',
  [ROLES.CASHIER]:             '#2ecc71',
  [ROLES.KITCHEN]:             '#e67e22',
  [ROLES.CHEF]:                '#e74c3c',
  [ROLES.RECEPTION]:           '#9b59b6',
  [ROLES.GATE]:                '#e74c3c',
  [ROLES.HOUSEKEEPING]:        '#1abc9c',
  [ROLES.ACCOUNTANT]:          '#f39c12',
  [ROLES.GIFT_SHOP]:           '#16a085',
  [ROLES.RESTAURANT_MANAGER]:  '#8e44ad',
  [ROLES.STOREKEEPER]:         '#1a9ba0',
  [ROLES.MARKETING]:           '#e84393',
  [ROLES.HR]:                  '#8e44ad',
  [ROLES.SWIMMING_ATTENDANT]:  '#0ea5e9',
  [ROLES.KIDS_AMENITIES]:      '#f472b6',
};

// Roles that get a single primary page (not the multi-tab admin view)
export const PRIMARY_ONLY_ROLES = [
  ROLES.KITCHEN,
  ROLES.GATE,
  ROLES.HOUSEKEEPING,
  ROLES.CHEF,
  ROLES.MARKETING,
  ROLES.HR,
  ROLES.SWIMMING_ATTENDANT,
  ROLES.KIDS_AMENITIES,
];

// ── Payment Modes ─────────────────────────────────────────────────────────────
export const PAYMENT_MODES = [
  { value: 'cash',        label: 'Cash',         color: '#2ecc71' },
  { value: 'mpesa',       label: 'M-Pesa',        color: '#00b300' },
  { value: 'card',        label: 'Card / PDQ',    color: '#9b59b6' },
  { value: 'room_charge', label: 'Room Charge',   color: '#e67e22' },
  { value: 'stripe',      label: 'Stripe',        color: '#635bff' },
  { value: 'bank',        label: 'Bank Transfer', color: '#2980b9' },
  { value: 'cheque',      label: 'Cheque',        color: '#7f8c8d' },
];

export const PAYMENT_MODE_MAP = Object.fromEntries(
  PAYMENT_MODES.map(m => [m.value, m]),
);

// ── Room Types ────────────────────────────────────────────────────────────────
export const ROOM_TYPES = ['standard', 'deluxe', 'suite', 'family', 'presidential'];

// ── Pool Rates ────────────────────────────────────────────────────────────────
export const POOL_RATES = [
  { type: 'adult_resident',    label: 'Adult Resident',       price: 500  },
  { type: 'child_resident',    label: 'Child Resident (<12)', price: 300  },
  { type: 'adult_nonresident', label: 'Adult Non-Resident',   price: 1000 },
  { type: 'child_nonresident', label: 'Child Non-Resident',   price: 600  },
  { type: 'hotel_guest',       label: 'Hotel Guest (FOC)',    price: 0    },
];

// ── Guest Status ──────────────────────────────────────────────────────────────
export const GUEST_STATUSES = {
  CHECKED_IN:  'checked-in',
  CHECKED_OUT: 'checked-out',
  RESERVED:    'reserved',
};

// ── Order Status ──────────────────────────────────────────────────────────────
export const ORDER_STATUSES = {
  PENDING:   'pending',
  PREPARING: 'preparing',
  READY:     'ready',
  SERVED:    'served',
};

// ── API Pagination ────────────────────────────────────────────────────────────
export const DEFAULT_PAGE_SIZE = 100;

// ── Polling Intervals (ms) ────────────────────────────────────────────────────
export const POLL_FAST   = 15_000;   // notifications, active orders
export const POLL_NORMAL = 30_000;   // most data
export const POLL_SLOW   = 60_000;   // reports, summaries

// ── Currency ──────────────────────────────────────────────────────────────────
export const BASE_CURRENCY       = 'KES';
export const CURRENCY_LOCALE     = 'en-KE';
export const CURRENCY_MIN_DIGITS = 2;
